<i18n src="./resources/crm/notes-locales.json"></i18n>

<template>
  <div class="crm-notes-container">
    <div v-show="!isLoading" class="inset-fade-effect" :dark="$vuetify.theme.dark" />
    <div v-if="isLoading" class="crm-notes-loading">
      <v-progress-linear color="var(--v-accent-base)" indeterminate height="4"></v-progress-linear>
      <div v-once class="text-center pa-5 opacity-75">{{ $t('loading') }}</div>
    </div>
    <div v-else class="crm-notes">
      <v-subheader class="crm-notes-header">
        <v-btn absolute right small text class="mr-n4 crm-new-note-button surface lighten-1" @click="showNewNoteInput">
          <v-icon left small>mdi-pencil</v-icon>
          <span v-once>{{ $t('newNoteActionLabel') }}</span>
        </v-btn>
      </v-subheader>
      <v-expand-transition>
        <v-row v-show="expandNoteInput" class="crm-notes-content-container">
          <v-list-item class="crm-note-item">
            <v-list-item-avatar class="crm-note-avatar">
              <EcsAvatar :contact-id="loggedInAgent.id" :size="40" no-animation />
            </v-list-item-avatar>
            <v-list-item-content class="crm-note-content">
              <EcsSubmitInput
                ref="noteInput"
                class="crm-note-input"
                :placeholder="$t('newNotePlaceholder')"
                :disable-multiline-shadow="true"
                icon-tooltip="tooltips.add"
                @onSubmit="addNote"
              />
            </v-list-item-content>
          </v-list-item>
        </v-row>
      </v-expand-transition>
      <div v-if="notes.length">
        <v-row class="crm-notes-content-container">
          <v-list color="transparent" flat dense>
            <template v-if="notes.length">
              <transition-group name="notes-list">
                <v-list-item v-for="note in notes" :key="note.createTime" class="crm-note-item">
                  <v-list-item-avatar class="crm-note-avatar">
                    <EcsAvatar :contact-id="note.creatorId" :alt="note.creatorName" :size="40" no-animation />
                  </v-list-item-avatar>
                  <v-list-item-content class="crm-note-content">
                    <v-list-item-title>
                      <strong>{{ note.creatorName }}</strong>
                      <span class="grey--text text--lighten-1 crm-notes-time">
                        {{ getNoteCreationTimeAgo(note.createTime) }}
                      </span>
                    </v-list-item-title>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <v-list-item-subtitle class="crm-note-text" v-html="note.note"> </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </transition-group>
            </template>
          </v-list>
        </v-row>
      </div>
      <div v-else class="crm-empty-notes">{{ $t('emptyNotes') }}</div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsSubmitInput from '@/components/ecs-submit-input/EcsSubmitInput';
import { parseMessage } from '@/utils';
import { NOTE_TYPE } from '@/constants/contactConstants';
import ContactService from '@/services/contactService';

export default {
  name: 'InteractionCrmNotes',

  components: { EcsAvatar, EcsSubmitInput },

  props: {
    contactId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      expandNoteInput: false,
      notes: [],
      isLoading: true
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent'])
  },

  async mounted() {
    this.notes = await this.getNotes();
    this.isLoading = false;
  },

  methods: {
    async getNotes() {
      try {
        const response = await ContactService.getContactNotes(this.contactId);
        return response.data.filter(note => note.noteType === NOTE_TYPE.note);
      } catch (error) {
        return [];
      }
    },

    showNewNoteInput() {
      this.expandNoteInput = !this.expandNoteInput;

      if (!this.expandNoteInput) {
        return;
      }

      setTimeout(() => {
        this.$refs.noteInput.$el.querySelector('textarea').focus();
      }, 250);
    },

    hideNoteInput() {
      this.expandNoteInput = false;
    },

    getNoteCreationTimeAgo(creationTime) {
      return moment(creationTime || Date.now()).fromNow();
    },

    addNote(note) {
      if (!note) {
        return;
      }

      this.hideNoteInput();

      ContactService.createContactNote(this.contactId, {
        note: parseMessage(note)
      })
        .then(async response => {
          this.notes = await this.getNotes();
        })
        .catch(() => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('addNewNoteError'),
            type: 'error'
          });
        });
    }
  }
};
</script>

<style scoped>
.crm-notes-container {
  height: 100%;
  overflow: auto;
}

.crm-notes {
  width: 100%;
  padding: 12px;
}

.crm-notes-header {
  height: 26px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  padding: 16px;
  position: relative;
  border-radius: 16px;
}

.crm-notes-content-container .v-list {
  max-width: 100%;
}

.crm-new-note-button {
  font-weight: bolder;
}

.crm-note-item {
  margin: 2px 0;
}

.crm-note-time {
  opacity: 0.8;
  font-size: 10px;
}

.crm-note-avatar {
  align-self: start;
  padding: 0;
}

.crm-note-content {
  align-self: center;
  margin: 0;
  padding: 0 !important;
}

.crm-note-text {
  text-overflow: initial;
  overflow: hidden;
  white-space: initial;
  word-break: break-word;
}

.crm-notes-list-enter-active,
.crm-notes-list-leave-active {
  transition: all 250ms;
}

.crm-notes-list-enter,
.crm-notes-list-leave-to {
  opacity: 0;
  transform: translateY(-36px);
}

.crm-empty-notes {
  border-radius: 8px !important;
  border: 2px dashed;
  text-align: center;
  opacity: 0.5;
  margin-top: 16px;
  padding: 16px;
}
</style>
